<template>
  <v-dialog v-model="open" max-width="720" scrollable>
    <v-card class="kf-perf">
      <v-card-title class="kf-perf__title">
        <LucideIcon name="chart-line" :size="20" class="mr-2" />
        {{ template ? template.name : "Performance" }}
        <v-spacer />
        <v-select
          v-model="days"
          :items="[7, 30, 90, 365]"
          hide-details
          dense
          outlined
          class="kf-perf__days"
          @change="load"
        />
      </v-card-title>

      <v-card-text class="kf-perf__body">
        <ErrorState v-if="error" :error="error" @retry="load" />
        <v-skeleton-loader v-else-if="loading" type="image" />

        <template v-else-if="data">
          <div class="kf-perf__tiles">
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ data.sent }}</div>
              <div class="kf-perf__k">Attempted</div>
            </div>
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ data.accepted }}</div>
              <div class="kf-perf__k">Accepted by provider</div>
            </div>
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ pct(data.rates.opened) }}</div>
              <div class="kf-perf__k">Opened</div>
            </div>
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ pct(data.rates.clicked) }}</div>
              <div class="kf-perf__k">Clicked</div>
            </div>
          </div>

          <!-- "We do not know" is not "nobody opened it". A box whose provider
               callbacks are not configured has an empty event table, and
               reporting 0% there would condemn copy nobody has measured. -->
          <div v-if="noSignal" class="kf-perf__nosignal">
            No engagement events recorded for this template yet. A rate shows as
            “—” rather than 0% because nothing has been measured — that is not the
            same as nobody responding.
          </div>

          <div v-if="eventRows.length" class="kf-perf__section">
            <div class="kf-perf__head">Events</div>
            <apexchart
              type="bar"
              height="200"
              :options="chartOptions"
              :series="chartSeries"
            />
          </div>

          <div class="kf-perf__section">
            <div class="kf-perf__head">
              Event messages using this copy
              <span v-if="!(data.rules || []).length" class="kf-perf__muted">
                — none; it is only sent by the growth agent
              </span>
            </div>
            <v-simple-table v-if="(data.rules || []).length" dense>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Channel</th>
                  <th>Label</th>
                  <th>Live</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rule in data.rules" :key="rule.id">
                  <td class="kf-mono">{{ rule.stage }} +{{ rule.offset_minutes }}m</td>
                  <td>{{ rule.channel }}</td>
                  <td>{{ rule.label }}</td>
                  <td>
                    <StatusChip :status="rule.is_active ? 'Active' : 'Inactive'" small />
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="kf-perf__actions">
        <v-spacer />
        <v-btn text small class="text-none" @click="open = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * How one piece of copy has actually done.
 *
 * Rates come back null rather than zero when nothing has been measured, and this
 * renders that as "—" with a sentence explaining why. That distinction is the
 * whole reason this screen is trustworthy: the engagement table is populated by
 * provider webhooks, and a box where those are not configured has no data at all
 * — showing 0% open would make good copy look dead.
 */
import ErrorState from "@/components/karrofin/ErrorState.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import api from "@/app-js/karrofin/api";

export default {
  name: "TemplatePerformanceDialog",
  components: { ErrorState, StatusChip },
  props: {
    value: { type: Boolean, default: false },
    template: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
      days: 30,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    eventRows() {
      const events = (this.data && this.data.events) || {};
      return Object.keys(events).map((k) => ({ key: k, n: events[k] }));
    },
    noSignal() {
      return !!this.data && !this.eventRows.length;
    },
    chartSeries() {
      return [{ name: "Events", data: this.eventRows.map((r) => r.n) }];
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "45%" } },
        dataLabels: { enabled: false },
        colors: ["#0277BD"],
        xaxis: { categories: this.eventRows.map((r) => r.key) },
        yaxis: { labels: { formatter: (v) => Math.round(v) } },
        grid: { borderColor: "#EEE" },
      };
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.data = null;
        this.load();
      }
    },
  },
  methods: {
    /** Null means unmeasured, and has to read differently from zero. */
    pct(rate) {
      if (rate === null || rate === undefined) return "—";
      return Math.round(rate * 1000) / 10 + "%";
    },
    load() {
      if (!this.template) return;
      this.loading = true;
      this.error = null;
      Promise.resolve()
        .then(() => api.getTemplatePerformance(this.template.id, this.days))
        .then((data) => {
          this.data = data;
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-perf__title {
  font-size: var(--kf-fs-title);
  font-weight: 700;
  padding-bottom: 8px;
}
.kf-perf__days {
  max-width: 110px;
  flex: 0 0 auto;
}
.kf-perf__body {
  max-height: 64vh;
}
.kf-perf__tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.kf-perf__tile {
  padding: 10px 12px;
  border: 1px solid #EEE;
  border-radius: 6px;
}
.kf-perf__n {
  font-size: 20px;
  font-weight: 700;
  color: #000;
}
.kf-perf__k {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-perf__nosignal {
  padding: 10px 12px;
  border-radius: 6px;
  background: #F5F5F5;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-bottom: 14px;
}
.kf-perf__section {
  margin-top: 16px;
}
.kf-perf__head {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  margin-bottom: 6px;
}
.kf-perf__muted {
  font-weight: 400;
  color: var(--kf-text-muted);
}
.kf-mono {
  font-family: monospace;
  font-size: var(--kf-fs-label);
}
.kf-perf__actions {
  padding: 8px 16px 14px;
}
</style>
